import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { type Theme, ThemeProvider } from '@mui/material/styles';
import Sidebar from 'components/layout/SideBar/Sidebar';
import { useSidebar } from 'contexts/SidebarContext';
import useCurrentRoute from 'hooks/useCurrentRoute';
import { PostHogErrorBoundary } from 'posthog-js/react';
import type { FC, JSX } from 'react';

import styles from './Layout.module.scss';
import DefaultTheme from './themes/DefaultTheme';

const THEMES: Theme[] = [DefaultTheme];

interface Props {
  children: JSX.Element;
}

const PrivateLayout: FC<Props> = ({ children }) => {
  const { drawerWidth } = useSidebar();

  return (
    <Box sx={{ display: { xs: 'block', md: 'flex' } }}>
      <Sidebar />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: {
            xs: '100%',
            md: `calc(100% - ${drawerWidth}px)`,
          },
          maxHeight: '100vh',
          overflow: 'hidden',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'backgroundGrey.main',
            minHeight: '100vh',
            width: '100%',
            overflow: 'auto',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

const Layout: FC<Props> = ({ children }) => {
  const { private: privateRoute, noLayout } = useCurrentRoute();
  const theme = THEMES[0];

  if (!privateRoute || noLayout) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className={styles.ComponentContainer}>{children}</div>
      </ThemeProvider>
    );
  }

  return (
    <PostHogErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <PrivateLayout>{children}</PrivateLayout>
      </ThemeProvider>
    </PostHogErrorBoundary>
  );
};

export default Layout;
